import type { ReactNode, SelectHTMLAttributes } from "react";
import type { RuleSet, Tier } from "../../api/contracts";
import { CoverageBar } from "./CoverageBar";
import { OpenTimeTimeline } from "./OpenTimeTimeline";
import { CloseButton, Help, Panel, PanelRow, Pick, RowText, Toggle, cx } from "./ui";
import { HELP, RuleExplainer } from "./RuleExplainer";
import { ProtectedHoursEditor } from "./ProtectedHoursEditor";
import type { WeekHours } from "../../btb/protected-hours";
import { addLargerTier, canAddLargerTier, lockExample, lookaheadLabel, removeTier, setTierBlock, setTierStart, stepChoices, tierChoices, tierRangeText, type Choice } from "../lib/rules";
import { formatMinutes } from "../lib/format";
import { DEFAULT_START_LOCK_HOURS_BEFORE, END_LOCK_ON_HOURS_BEFORE, RULE_STEPS, hoursText } from "../../btb/constants";

/** A dropdown that sits inside a sentence or at the right of a row: the Figma pick box, as wide as its words. */
function Blank({ choices, value, onPick, className, ...props }: { choices: Choice[]; value: number; onPick: (v: number) => void } & Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange">) {
  return (
    <Pick {...props} value={value} onChange={(e) => onPick(Number(e.target.value))} className={className}>
      {choices.map((c) => <option key={c.value} value={c.value} disabled={c.disabled}>{c.label}</option>)}
    </Pick>
  );
}

const Sentence = ({ children }: { children: ReactNode }) => <div className="flex flex-wrap items-center gap-2 text-base font-semibold leading-6 text-hs-text">{children}</div>;

/** A row whose control is a toggle at the left and, when on, a dropdown at the right. */
function SwitchRow({ checked, onChange, label, text, hint, children }: { checked: boolean; onChange: (on: boolean) => void; label: string; text: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  return (
    <PanelRow>
      <div className="flex min-w-0 flex-[1_1_20rem] items-center gap-4">
        <Toggle checked={checked} label={label} onChange={onChange} />
        <RowText label={text} hint={hint} />
      </div>
      {children}
    </PanelRow>
  );
}

/** One schedule lock: a switch, the words, and the hours at the right when on. */
function LockRow({ edge, hours, steps, onHours, turnOnAt }: { edge: "start" | "end"; hours: number | null; steps: readonly number[]; onHours: (h: number | null) => void; turnOnAt: number }) {
  const verb = edge === "start" ? "starts" : "ends";
  const on = hours !== null;
  const text = on ? `Lock ${edge} time` : `${edge === "start" ? "Start" : "End"} times are not locked`;
  const hint = !on
    ? (edge === "start" ? "Off: start-of-shift blocks can change right up until the shift starts." : "Off: end-of-shift blocks keep adjusting as bookings come in.")
    : (edge === "start" ? `A 9am shift locks at ${lockExample(9, hours)}.` : `A 6pm finish locks at ${lockExample(18, hours)}.`);
  return (
    <SwitchRow checked={on} label={`Lock ${edge} times`} onChange={(next) => onHours(next ? turnOnAt : null)} text={text} hint={hint}>
      {on && (
        <span className="flex items-center gap-3">
          <Blank aria-label={`Hours before the shift ${verb}`} choices={stepChoices(steps, hours, hoursText)} value={hours} onPick={onHours} />
          <span className="text-sm font-medium text-hs-text-muted">before the shift {verb}</span>
        </span>
      )}
    </SwitchRow>
  );
}

/** Controlled editor. Validation happens in the parent (`validateRuleSet`, then `checkRuleSteps`); this only renders the errors it is given. */
export function RuleEditor({ value, onChange, errors, hours = null, hoursSource = "default" }: { value: RuleSet; onChange: (next: RuleSet) => void; errors: string[]; hours?: WeekHours | null; hoursSource?: "boulevard" | "default" }) {
  const setTiers = (tiers: Tier[]) => onChange({ ...value, tiers });
  const set = <K extends keyof RuleSet>(key: K) => (v: RuleSet[K]) => onChange({ ...value, [key]: v });
  return (
    <div className="space-y-7">
      <Panel title="What counts as open time" subtitle="Only the open stretch before the first appointment or after the last one. Gaps between appointments are never blocked.">
        <OpenTimeTimeline />
      </Panel>

      <Panel padding="none" title="How long a BTB block to add" subtitle="The more open time at the start or end of a shift, the longer the block. Each rule applies until the next one takes over, so open-time thresholds must keep increasing."
        actions={<Help label="What do the rules mean?">{HELP.tiers}</Help>}>
        <div className="px-6 pb-6 pt-1"><CoverageBar tiers={value.tiers} /></div>
        {value.tiers.map((t, i) => {
          const choices = tierChoices(value.tiers, i);
          return (
            <PanelRow key={i}>
              <div className="min-w-0 flex-[1_1_32rem] space-y-1">
                <Sentence>
                  <span>Add a</span>
                  <Blank aria-label="BTB block length" choices={choices.block} value={t.blockMinutes} onPick={(v) => setTiers(setTierBlock(value.tiers, i, v))} />
                  <span>BTB block when a shift has at least</span>
                  <Blank aria-label="Open time at the start or end of the shift" choices={choices.start} value={t.minGap} onPick={(v) => setTiers(setTierStart(value.tiers, i, v))} />
                  <span>open at the start or end.</span>
                </Sentence>
                <p className="text-xs font-medium text-hs-text-muted">{tierRangeText(value.tiers, i)}</p>
              </div>
              {value.tiers.length > 1 && <CloseButton label={`Remove the rule starting at ${formatMinutes(t.minGap)}`} onClick={() => setTiers(removeTier(value.tiers, i))} />}
            </PanelRow>
          );
        })}
        <PanelRow>
          <button type="button" disabled={!canAddLargerTier(value.tiers)} onClick={() => setTiers(addLargerTier(value.tiers))}
            className={cx("flex h-[61px] w-full items-center justify-center rounded-2xl border border-dashed border-hs-primary text-sm font-semibold text-hs-primary hover:bg-hs-primary-soft/40 disabled:cursor-not-allowed disabled:opacity-50")}>
            <span aria-hidden>+&nbsp;</span>Add a larger BTB block
          </button>
        </PanelRow>
      </Panel>

      <Panel padding="none" title="When a block comes off" subtitle="Both conditions must be true before a BTB block is removed."
        actions={<Help label="When do blocks come off?">{HELP.removalPct} {HELP.removalGap}</Help>}>
        <PanelRow>
          <RowText label="Day is booked at least" hint={`Days already past this get no new blocks.`} />
          <Blank aria-label="How booked the day is" choices={stepChoices(RULE_STEPS.removalUtilizationPct, value.removalUtilizationPct, (v) => `${v}%`)} value={value.removalUtilizationPct} onPick={set("removalUtilizationPct")} />
        </PanelRow>
        <PanelRow>
          <RowText label="Open time next to the block is under" hint="A busy day with one big open stretch keeps its block." />
          <Blank aria-label="Open time next to the block" choices={stepChoices(RULE_STEPS.removalMinGapMinutes, value.removalMinGapMinutes, formatMinutes)} value={value.removalMinGapMinutes} onPick={set("removalMinGapMinutes")} />
        </PanelRow>
      </Panel>

      <Panel padding="none" title="Shifts and planning" subtitle="Which shifts get blocks, and how far ahead they're planned."
        actions={<><Help label="Why skip short shifts?">{HELP.minShift}</Help><Help label="How far ahead should I plan?">{HELP.lookahead}</Help></>}>
        <PanelRow>
          <RowText label="Skip shifts shorter than" hint="Short shifts never get a BTB block." />
          <Blank aria-label="Shortest shift that gets BTB blocks" choices={stepChoices(RULE_STEPS.minShiftMinutes, value.minShiftMinutes, formatMinutes)} value={value.minShiftMinutes} onPick={set("minShiftMinutes")} />
        </PanelRow>
        <PanelRow>
          <RowText label="Plan blocks ahead" hint="Includes today. Shortening removes blocks placed further out." />
          <Blank aria-label="How far ahead to plan" choices={stepChoices(RULE_STEPS.lookaheadDays, value.lookaheadDays, lookaheadLabel)} value={value.lookaheadDays} onPick={set("lookaheadDays")} />
        </PanelRow>
      </Panel>

      <Panel padding="none" title="Schedule locks" subtitle="Once locked, a block is renamed “DNB” and stays exactly as it is. Counted back from each shift's own start or end, so an early shift locks earlier than a late one."
        actions={<Help label="Why lock the schedule?">{HELP.locks}</Help>}>
        <LockRow edge="start" hours={value.startLockHoursBefore} steps={RULE_STEPS.startLockHoursBefore} onHours={set("startLockHoursBefore")} turnOnAt={DEFAULT_START_LOCK_HOURS_BEFORE} />
        <LockRow edge="end" hours={value.endLockHoursBefore} steps={RULE_STEPS.endLockHoursBefore} onHours={set("endLockHoursBefore")} turnOnAt={END_LOCK_ON_HOURS_BEFORE} />
      </Panel>

      <ProtectedHoursEditor value={value.protectedHours} onChange={set("protectedHours")} hours={hours} hoursSource={hoursSource} />

      <Panel padding="none" title="AI booking" subtitle={HELP.aiBooking}>
        <SwitchRow checked={value.aiBookingEnabled} label="Let the AI booking assistant book into BTB time" onChange={set("aiBookingEnabled")}
          text="Let the AI booking assistant book into BTB time"
          hint={value.aiBookingEnabled ? "On: when a client asks for a time a BTB block is holding, the assistant can open that block and book them." : "Off: the AI booking assistant books only open time here."} />
      </Panel>

      <Panel padding="none" title="Managers' BTB blocks" subtitle="BTB blocks your team places by hand at the start or end of a shift.">
        <SwitchRow checked={value.adoptManualBtb} label="Manage BTB blocks your team places by hand" onChange={set("adoptManualBtb")}
          text="Manage BTB blocks your team places by hand"
          hint={value.adoptManualBtb
            ? "BTB blocks your team places by hand at the start or end of a shift are managed like ours. We leave each one as you set it until a booking changes the gap, then keep, shorten or remove it by these rules. A shortened one becomes an Auto - BTB block."
            : "Off: blocks your team places by hand are left alone."} />
      </Panel>

      <Panel padding="none" title="Keep someone in the salon" subtitle="Whether BTB blocks may cover everyone on shift at once.">
        <SwitchRow checked={value.keepSomeoneIn} label="Never block everyone at once" onChange={set("keepSomeoneIn")}
          text="Never block everyone at once"
          hint={value.keepSomeoneIn
            ? "When two or more people are on shift, all but one can get a BTB block, and who stays open takes turns through the week. Someone working alone keeps an open schedule."
            : "Off: each person's BTB blocks are planned on their own, so everyone on shift can be blocked at the same time."} />
      </Panel>

      <RuleExplainer rules={value} />
      {errors.length > 0 && <ul role="alert" className="list-disc rounded-lg border border-hs-danger/40 bg-hs-danger-soft p-4 pl-8 text-sm font-medium text-hs-danger">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
    </div>
  );
}
